// ─────────────────────────────────────────────────────────────
// App Configuration
//
// All app-wide constants live here — URLs, storage keys, event names,
// theme names, timing values, and UI limits.
// Import from this file rather than scattering magic values across components.
// ─────────────────────────────────────────────────────────────

// ── API URLs ──────────────────────────────────────────────────
// Today: all services run as one monolith at VITE_API_BASE.
// Tomorrow: split into microservices by setting individual env vars.
const MONOLITH = (import.meta.env.VITE_API_BASE as string) ?? 'http://localhost:3000/api/v1'

export const SERVICE_URLS = {
  auth:          (import.meta.env.VITE_AUTH_SERVICE_URL          as string) ?? MONOLITH,
  discussions:   (import.meta.env.VITE_DISCUSSIONS_SERVICE_URL   as string) ?? MONOLITH,
  comments:      (import.meta.env.VITE_COMMENTS_SERVICE_URL      as string) ?? MONOLITH,
  ai:            (import.meta.env.VITE_AI_SERVICE_URL            as string) ?? MONOLITH,
  notifications: (import.meta.env.VITE_NOTIFICATIONS_SERVICE_URL as string) ?? MONOLITH,
  users:         (import.meta.env.VITE_USERS_SERVICE_URL         as string) ?? MONOLITH,
  payments:      (import.meta.env.VITE_PAYMENTS_SERVICE_URL      as string) ?? MONOLITH,
  sse:           (import.meta.env.VITE_SSE_SERVICE_URL           as string) ?? MONOLITH,
} as const

// Convenience — resolves to the monolith URL unless individually overridden
export const API_BASE = MONOLITH

export type ServiceName = keyof typeof SERVICE_URLS

// ── localStorage / sessionStorage keys ───────────────────────
// Centralised so a typo in one place doesn't silently break persistence
export const STORAGE_KEYS = {
  theme:             'ekkam-theme',
  auth:              'ekkam_auth',
  refreshToken:      'ekkam_refresh_token',
  recentSearches:    'ekkam_recent_searches',
  firstVisit:        'ekkam_first_visit_shown',
  engagementShown:   'ekkam_engagement_shown',
  guestInteractions: 'ekkam_guest_interactions',
  returnTo:          'ekkam_return_to',
} as const

// ── Custom DOM event names ─────────────────────────────────────
// Dispatched in one component, listened for in another with no TypeScript link
// between them — centralizing here means a typo on either end fails to compile
// instead of silently breaking the feature at runtime.
export const DOM_EVENTS = {
  guestInteraction:     'ekkam:interaction',
  notification:         'ekkam:notification',
  chatRate:             'ekkam:chat-rate',
  sseNewReply:          'sse:new_reply',
  sseLikeUpdate:        'sse:like_update',
  sseCommentEdited:     'sse:comment_edited',
  deeplinkOpenReplies:  'deeplink:open-replies',
} as const

// ── Theme, profile limits, discussion limits, taxonomy caps, report reasons ──
// Single source of truth lives in @ekkam/shared — imported here so the
// rest of the app can keep importing from this file without changes.
export {
  VALID_THEMES,
  DEFAULT_THEME,
  MAX_DISPLAY_NAME_LEN,
  MAX_BIO_LEN,
  DISCUSSION_TITLE_MIN,
  DISCUSSION_TITLE_MAX,
  DISCUSSION_CONTENT_MIN,
  DISCUSSION_CONTENT_MAX,
  MAX_DISCUSSION_TAGS,
  MAX_DISCUSSION_CATEGORIES,
  REPORT_REASONS,
} from '@ekkam/shared'
export type { ThemeName, ReportReason } from '@ekkam/shared'

// ── Feature flags ────────────────────────────────────────────────
export const ENABLE_DMS     = import.meta.env.VITE_ENABLE_DMS     === 'true'
export const ENABLE_SUPPORT = import.meta.env.VITE_ENABLE_SUPPORT === 'true'

// ── Timing (milliseconds) ─────────────────────────────────────
// Refresh access token 3 min before expiry (JWT lifetime = 15 min)
export const TOKEN_REFRESH_INTERVAL_MS = 12 * 60 * 1000

// Delay before redirecting on OAuth error or success
export const REDIRECT_DELAY_MS = 2000

// First-visit login prompt fires after this idle period
export const FIRST_VISIT_PROMPT_DELAY_MS = 2000

// SSE reconnect backoff
export const SSE_RECONNECT_INITIAL_MS        = 1000
export const SSE_RECONNECT_MAX_MS            = 8000
export const SSE_RECONNECT_FIRST_CAP_MS      = 2000
export const SSE_TOKEN_RECONNECT_DEBOUNCE_MS = 100

// ── Auth retry ────────────────────────────────────────────────
export const AUTH_MAX_RETRIES        = 5
export const AUTH_MAX_RETRY_DELAY_MS = 16000
export const AUTH_RETRY_BASE_DELAY_MS = 1000 // base delay for exponential backoff

// ── Guest engagement triggers ─────────────────────────────────
export const GUEST_ENGAGEMENT_INTERACTION_THRESHOLD = 4
export const GUEST_SCROLL_PROMPT_THRESHOLD_PX       = 400

// ── Polling intervals ─────────────────────────────────────────
export const POLL_AGENTS_MS        = 60_000
export const POLL_NOTIFICATIONS_MS = 60_000

// ── Pagination / UI limits ────────────────────────────────────
export const COMMENTS_PAGE_SIZE      = 10
export const REPLIES_PAGE_SIZE       = 5
export const NOTIFICATIONS_PAGE_SIZE = 15
export const LIKERS_TOOLTIP_MAX      = 6

// ── Content display limits ────────────────────────────────────
export const COMMENT_PREVIEW_REPLIES  = 2
export const COMMENT_CONTENT_TRUNCATE = 280
export const NOTIFICATION_MAX_BADGE   = 9

// ── Pagination — discussions ──────────────────────────────────
export const DISCUSSIONS_PAGE_SIZE   = 5
export const ADMIN_USERS_PAGE_SIZE   = 15
export const NAV_TRENDING_COUNT      = 5
export const NAV_SEARCH_COUNT        = 5
export const NAV_RECENT_SEARCHES_MAX = 8

// ── Debounce delays ───────────────────────────────────────────
export const SEARCH_DEBOUNCE_MS       = 300
export const ADMIN_SEARCH_DEBOUNCE_MS = 300
export const MENTION_DEBOUNCE_MS      = 180

// ── Other UI timings ─────────────────────────────────────────
export const SAVED_BANNER_TIMEOUT_MS = 3000
export const RTK_CACHE_LIFETIME_S    = 300

// ── Shared UI messages ────────────────────────────────────────
export const MESSAGES = {
  SAVE_FAILED: 'Failed to save. Please try again.',
} as const

// ── Mention dropdown ─────────────────────────────────────────
export const MENTION_DROPDOWN_WIDTH = 256

// ── User hover card ───────────────────────────────────────────
export const USER_HOVER_CARD_ENABLED        = true
export const USER_HOVER_CARD_WIDTH          = 264
export const DROPDOWN_VIEWPORT_MARGIN       = 12
export const USER_HOVER_CARD_OPEN_DELAY_MS  = 300
export const USER_HOVER_CARD_CLOSE_DELAY_MS = 120

// ── Pinning ───────────────────────────────────────────────────
export const PINNING_ENABLED = true
export const PIN_COLOR       = '#f97316'
export const PIN_COLOR_BG    = 'rgba(249,115,22,0.15)'

// ── Like tooltip ─────────────────────────────────────────────
export const LIKE_TOOLTIP_HOVER_DELAY_MS = 300

// ── Feature flags (hardcoded — change requires redeploy) ─────
export const DISCUSSION_EDIT_ENABLED = false
export const REPORTING_ENABLED       = true

// ── Semantic UI colors ───────────────────────────────────────
export const COLOR_ERROR   = '#ef4444'
export const COLOR_SUCCESS = '#22c55e'
export const COLOR_WARNING = '#eab308'
export const COLOR_CYAN    = '#06b6d4'

export const COLOR_ERROR_BG       = 'rgba(239,68,68,0.1)'
export const COLOR_ERROR_BORDER   = 'rgba(239,68,68,0.3)'
export const COLOR_SUCCESS_BG     = 'rgba(34,197,94,0.1)'
export const COLOR_SUCCESS_BORDER = 'rgba(34,197,94,0.3)'
export const COLOR_WARNING_BG     = 'rgba(234,179,8,0.1)'
export const COLOR_WARNING_BORDER = 'rgba(234,179,8,0.3)'

export const COLOR_ACCENT_ICON_BG      = 'rgba(124,58,237,0.12)'
export const COLOR_ACCENT_ICON_BORDER  = 'rgba(124,58,237,0.25)'
export const COLOR_CYAN_ICON_BG        = 'rgba(6,182,212,0.12)'
export const COLOR_CYAN_ICON_BORDER    = 'rgba(6,182,212,0.25)'
export const COLOR_SUCCESS_ICON_BG     = 'rgba(34,197,94,0.12)'
export const COLOR_SUCCESS_ICON_BORDER = 'rgba(34,197,94,0.25)'
export const COLOR_ERROR_ICON_BG       = 'rgba(239,68,68,0.12)'
export const COLOR_ERROR_ICON_BORDER   = 'rgba(239,68,68,0.25)'
export const COLOR_WARNING_ICON_BG     = 'rgba(234,179,8,0.12)'
export const COLOR_WARNING_ICON_BORDER = 'rgba(234,179,8,0.25)'

export const COLOR_AI_BG          = 'rgba(124,58,237,0.04)'
export const COLOR_AI_BORDER      = 'rgba(124,58,237,0.15)'
export const COLOR_AI_ICON_BG     = 'rgba(124,58,237,0.2)'
export const COLOR_AI_TAG_BG      = 'rgba(124,58,237,0.15)'
export const COLOR_ACCENT_ROLE_BG = 'rgba(124,58,237,0.15)'
export const COLOR_CYAN_ROLE_BG   = 'rgba(6,182,212,0.15)'

// ── Sandbox response waiter (agent testing — AgentFormModal's Sandbox tab) ──
export const SANDBOX_RESPONSE_TIMEOUT_S          = 30
export const SANDBOX_INVOCATION_POLL_MS          = 2000
export const SANDBOX_TIMEOUT_WARNING_THRESHOLD_S = 5

// ── Agent knowledge-base uploads ──────────────────────────────
export const AGENT_DOC_MAX_FILES          = 5
export const AGENT_DOC_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024
export const FILE_PROCESSING_POLL_MS      = 3000

// ── Copy-to-clipboard feedback ────────────────────────────────
export const COPY_FEEDBACK_MS = 2000

// ── Admin panels ───────────────────────────────────────────────
export const ADMIN_SAVED_BANNER_TIMEOUT_MS = 2500

// ── Chat ──────────────────────────────────────────────────────
// Client-side display only — the real limit is enforced server-side by
// checkAISpendingCap; these must stay in sync with that config or the UI
// will show a limit that doesn't match what's actually enforced.
export const CHAT_RATE_LIMIT_PER_MIN = 20
export const CHAT_RATE_WINDOW_MS     = 60_000
export const CHAT_RESPONSE_TIMEOUT_MS = 60_000
export const CHAT_SEND_TIMEOUT_MS     = 45_000

// ── Category badges / icon inputs ─────────────────────────────
export const CATEGORY_BADGE_PREVIEW_MAX = 2
export const EMOJI_ICON_MAX_LEN         = 4

// ── Content preview truncation ─────────────────────────────────
export const AI_REPLY_PREVIEW_TRUNCATE = 120

// ── AI summary ─────────────────────────────────────────────────
export const AI_SUMMARY_ERROR_RESET_MS = 3000

// ── Dropdown UX ────────────────────────────────────────────────
export const DROPDOWN_AUTOFOCUS_DELAY_MS = 50
